/**
 * PublicShell — layout wrapper for public marketing/SEO routes.
 *
 * Uses the same authoritative `Navbar` as the rest of the app so every
 * public route (home, marketplace, category pages, trust, etc.) shares
 * one header, one CTA, one mobile menu.  The header itself adapts its
 * primary CTA based on `launch_mode` (early-access vs list-your-coach).
 */
import { Suspense } from "react";
import Navbar from "@/components/Navbar";
import PublicFooter from "./PublicFooter";
import PageFallback from "@/components/PageFallback";

/**
 * PublicShell — layout wrapper for public marketing routes.
 *
 * SEO / canonical strategy:
 *   Every public marketing page owns its <head> via an explicit
 *   `<SEO path="…" />` from ../SEO.jsx.  PublicShell intentionally does
 *   NOT emit its own <SEO> so that (a) exactly one <link rel="canonical">
 *   appears in the rendered head, and (b) no page-level canonical can be
 *   overridden by a layout default.  Pages without their own <SEO> fall
 *   back to the sitewide title / meta in /app/frontend/public/index.html.
 *
 *   Historical bug: an earlier PublicShell rendered <SEO/> unconditionally,
 *   which caused `/why-list-with-vanturis` to serve
 *   `<link rel="canonical" href="https://vanturis.com/">` in Production.
 *   That regression was resolved by making PublicShell head-agnostic.
 */
export default function PublicShell({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        <Suspense fallback={<PageFallback />}>{children}</Suspense>
      </main>
      <PublicFooter />
    </div>
  );
}
